import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../constants/axiosInstance';
import { AnnotatedSection, TableData, useOCRStore } from '../store/ocrStore';
import {
  createSimplifiedCellsForVisionAI,
  parseTextractResponse
} from '../helper/ocrUtils';
import { toast } from 'react-toastify';

const analyzeAnnotationWithTextract = async (annotation: AnnotatedSection, signal: AbortSignal) => {
  const base64Image = annotation.imageData.split(',')[1] || annotation.imageData;
  const { data } = await axiosInstance().post(`/ocr/annotateImage`, {
    ocr_image: `data:image/png;base64,${base64Image}`
  }, { signal });
  return data;
};

const analyzeContextWithVisionAI = async ({ imageData, simplifiedCells }: { imageData: string, simplifiedCells: any[] }, signal: AbortSignal) => {
  const base64Image = imageData.split(',')[1] || imageData;
  const { data } = await axiosInstance().post(`/ocr/getRowAndColumnHeadings`, {
    imageBase64: `data:image/png;base64,${base64Image}`,
    simplifiedCells: JSON.stringify(simplifiedCells)
  }, { signal });
  return data.data;
};

export const useOCRAnalysis = () => {
  const { t } = useTranslation();
  const { selectedTemplate, annotations, annotationIds, tables, addAnnotation, addTable, deleteAnnotationAndAssociatedTables } =
    useOCRStore(state => ({
      selectedTemplate: state.selectedTemplate,
      annotations: state.annotations,
      annotationIds: state.annotationIds,
      tables: state.tables,
      addAnnotation: state.addAnnotation,
      addTable: state.addTable,
      deleteAnnotationAndAssociatedTables: state.deleteAnnotationAndAssociatedTables
    }));
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAnnotation, setPendingAnnotation] = useState<AnnotatedSection | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const visionMutation = useMutation<any, unknown, { imageData: string; simplifiedCells: any[]; originalAnnotation: AnnotatedSection; textractData: any; signal: AbortSignal }>({
    mutationFn: ({ imageData, simplifiedCells, signal }) => analyzeContextWithVisionAI({ imageData, simplifiedCells }, signal),
    onSuccess: (visionData, { originalAnnotation, textractData }) => {
      const aiHeaderMap = new Map();
      visionData.aiHeaderAnalysis.forEach((item: any) => {
        if (item.cellId && item.headers && item.headers.row && item.headers.col) {
          aiHeaderMap.set(item.cellId, {
            rowHeader: item.headers.row,
            colHeader: item.headers.col
          });
        }
      });

      const finalParsedGrids = parseTextractResponse(textractData.data.Blocks, aiHeaderMap);
      const newTableIds: string[] = [];
      finalParsedGrids.forEach((tableObject, index) => {
        const tableId = `${originalAnnotation.id}-table-${index}`;
        newTableIds.push(tableId);
        const newTable: TableData = {
          id: tableId,
          annotationId: originalAnnotation.id,
          title: finalParsedGrids.length > 1
            ? t('logbookConfig.annotationTitleWithTable', 'Annotation #{{num}} (Table {{tableNum}})', { num: annotationIds.length + 1, tableNum: index + 1 })
            : t('logbookConfig.annotationTitle', 'Annotation #{{num}}', { num: annotationIds.length + 1 }),
          grid: tableObject.grid,
        };
        addTable(newTable);
      });

      addAnnotation(originalAnnotation);
      toast.success(t('logbookConfig.annotationCreatedSuccess', 'Annotation created successfully.'));
      closeModal();

      setTimeout(() => {
        const newAnnotationElement = document.getElementById(`annotation-preview-${originalAnnotation.id}`);
        newAnnotationElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
        if (newTableIds.length > 0) {
          const newTableElement = document.getElementById(`data-table-${newTableIds[0]}`);
          newTableElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    },
    onError: (error: any) => { throw new Error(error?.message || t('logbookConfig.analysisErrorOccurred', 'An error occurred while analyzing the annotation. Please try again.')) }
  });

  const textractMutation = useMutation({
    mutationFn: ({ annotation, signal }: { annotation: AnnotatedSection; signal: AbortSignal }) => analyzeAnnotationWithTextract(annotation, signal),
    onSuccess: (textractData, variables) => {
      const { annotation, signal } = variables;
      const backendAnnotationId = textractData?.data?.annotationId;

      const annotationWithBackendId: AnnotatedSection = {
        ...annotation,
        backendAnnotationId: backendAnnotationId,
      };
      const allGrids = parseTextractResponse(textractData.data.Blocks, new Map());

      if (allGrids.length === 0) {
        throw new Error(t('logbookConfig.notAValidImage', 'Not a valid image.'));
      }
      const simplifiedCells = createSimplifiedCellsForVisionAI(allGrids);

      visionMutation.mutate({
        imageData: annotationWithBackendId.imageData,
        simplifiedCells,
        originalAnnotation: annotationWithBackendId,
        parsedGrid: allGrids,
        signal: signal,
        textractData
      });
    },
    onError: (error) => {
    }
  });

  const handleAnnotationCreate = (annotation: AnnotatedSection) => {
    setPendingAnnotation(annotation);
    setModalOpen(true);
  };

  const handleAnalyzeConfirm = () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    if (pendingAnnotation) {
      textractMutation.mutate({ annotation: pendingAnnotation, signal: controller.signal });
    }
  };

  const closeModal = () => {
    if ((textractMutation.isPending || visionMutation.isPending) && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = null;
    setModalOpen(false);
    setPendingAnnotation(null);
    textractMutation.reset();
    visionMutation.reset();
  };

  const handleAnnotationDelete = (annotationId: string) => {
    deleteAnnotationAndAssociatedTables(annotationId)
  };

  const retryAnalysis = () => {
    if (pendingAnnotation) {
      textractMutation.reset();
      visionMutation.reset();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      textractMutation.mutate({ annotation: pendingAnnotation, signal: controller.signal });
    }
  };

  return {
    tables,
    selectedTemplate,
    annotations,
    annotationIds,
    modalOpen,
    pendingAnnotation,

    isAnalyzing: textractMutation.isPending || visionMutation.isPending,
    analysisError: textractMutation.error || visionMutation.error,

    handleAnnotationCreate,
    handleAnalyzeConfirm,
    handleModalClose: closeModal,
    handleAnnotationDelete,
    retryAnalysis
  };
};